import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const splitComparisonCardsSkill: Skill = {
    name: SKILL_NAMES.splitComparisonCards,
    kind: 'codeo',
    description:
        'Bring two cards in from opposite sides with mirrored 3D tilts for a side-by-side moment — ' +
        'before/after, us vs them, two features. For comparison scenes with exactly two subjects.',
    instructions: `# Split Comparison Cards

Two cards enter from their own sides of the frame and settle with opposing 3D tilts (one leaning left, one right), like two panels turned slightly toward the viewer. It frames a comparison the instant both land — before/after, old way/new way, plan A/plan B — and a gentle counter-phase float keeps both alive without distracting from the contrast.

## Markup

A perspective stage holds two cards. Each card's title and copy are their own text leaves.

\`\`\`html
<div style="position: absolute; inset: 0; display: flex; gap: 80px; align-items: center; justify-content: center;
       perspective: 1400px;">
  <div id="card-left" data-l5-allow-offscreen
       style="width: 560px; background: var(--l5-color-2, #f4f4f8); border-radius: 24px; padding: 48px;">
    <h3 id="card-left-title"
        style="margin: 0 0 12px; font-family: var(--l5-font-0), sans-serif; font-size: 44px; color: var(--l5-color-0, #101820);">Before</h3>
    <p id="card-left-copy"
       style="margin: 0; font-family: var(--l5-font-1), sans-serif; font-size: 32px; color: var(--l5-color-0, #101820);">The old way</p>
  </div>
  <div id="card-right" data-l5-allow-offscreen
       style="width: 560px; background: var(--l5-color-1, #7a6dff); border-radius: 24px; padding: 48px;">
    <h3 id="card-right-title"
        style="margin: 0 0 12px; font-family: var(--l5-font-0), sans-serif; font-size: 44px; color: var(--l5-color-0, #ffffff);">After</h3>
    <p id="card-right-copy"
       style="margin: 0; font-family: var(--l5-font-1), sans-serif; font-size: 32px; color: var(--l5-color-0, #ffffff);">The new way</p>
  </div>
</div>
\`\`\`

Each card carries \`data-l5-allow-offscreen\`: entering from its own side of the frame means starting with part of the card past the edge, and how far past depends on the card's width and where it rests — a wider pair than this one starts well outside the canvas. The mark says that reach is the design, and covers the card's contents too.

The accent card's title/copy use near-white text — check WCAG AA contrast (4.5:1 for the body copy, 3:1 for the larger title) against \`var(--l5-color-1, #7a6dff)\`; the default dark ink token often fails against an accent fill.

## Timeline code

Cards enter from their sides with mirrored \`rotationY\`, the right one staggered behind the left. Neither card has a CSS \`transform\` baked in, so the \`fromTo\` transform channels below have nothing to conflict with. The resting float then tweens only \`y\` on each card — GSAP tracks transform components separately, so the tilt (\`rotationY\`) the entrance left behind is preserved automatically, no hand-written transform needed. Opposite \`y\` directions put the two cards in counter-phase.

\`\`\`js
const tl = gsap.timeline({ paused: true });

tl.fromTo('#card-left',  { xPercent: -60, opacity: 0, rotationY: 35 },
    { xPercent: 0, opacity: 1, rotationY: 12, duration: 0.7, ease: 'power3.out' }, 1.3);
tl.fromTo('#card-right', { xPercent: 60, opacity: 0, rotationY: -35 },
    { xPercent: 0, opacity: 1, rotationY: -12, duration: 0.7, ease: 'power3.out' }, 1.6);

// Counter-phase resting float — GSAP keeps each card's entrance rotationY while tweening only y.
// The leg count must be EVEN so each card ends a yoyo back at y:0 for its exit; rounding to whole
// periods and doubling guarantees that (an odd count would strand a card at y:-8).
const FLOAT_START = 2.3, FLOAT_END = 7.0, PERIOD = 2.6;
const halfCycles = 2 * Math.max(1, Math.round((FLOAT_END - FLOAT_START) / PERIOD));
tl.to('#card-left',  { y: -8, duration: PERIOD / 2, ease: 'sine.inOut', yoyo: true, repeat: halfCycles - 1 }, FLOAT_START);
tl.to('#card-right', { y: 8,  duration: PERIOD / 2, ease: 'sine.inOut', yoyo: true, repeat: halfCycles - 1 }, FLOAT_START);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Tilt**: ±10–18° \`rotationY\` reads as "turned toward you" without distortion; beyond ~25° the far edge collapses. Shadow naturally falls away from the tilt — add a \`box-shadow\` offset to match if you want extra depth.
- **Stagger**: the second card 0.2–0.4s behind the first so they don't enter as one block; the winner/"after" card entering second draws the eye to it.
- **Badge accents**: pop a small pill on each card's inner edge with \`back.out(1.7)\` just after the cards land.
- **Asymmetric emphasis**: give the favored card the accent background and a slightly larger scale; mute the other with \`var(--l5-color-2, ...)\`.
- The resting float hands off from the entrance \`rotationY\` value — keep the same tilt in both so there's no jump when the float takes over.

## Pitfalls

- **\`perspective\` goes on the parent stage**, not the cards — without it \`rotationY\` looks flat.
- Exactly two subjects. Three-plus cards want \`${SKILL_NAMES.staggeredListReveal}\`, not opposing tilts — mirrored tilt only reads as comparison with a left and a right.
- The cards carry no CSS \`transform\` of their own — the entrance \`fromTo\` and the \`y\` float own that channel, and GSAP preserves the entrance's \`rotationY\` through the float automatically. Don't hand-write \`element.style.transform\` on an element GSAP is already transforming, or the two fight on seek.
- The yoyo leg count must be EVEN so both cards end back at \`y: 0\` for their exit — compute it as \`2 × (whole periods)\` (as above), never a bare \`round(range / halfPeriod)\`, which can land odd and strand a card at \`y: -8\`.
- Keep card copy short — a comparison the viewer can read at a glance. Two paragraphs side by side is a document, not a beat. Keep each title and copy line its own node rather than merging them.`,
};
